import { useContext } from "react";

import { Button, Skeleton, TooltipContent } from "@upstageai/ui";
import { observer } from "mobx-react-lite";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";

import { Icon, Icons } from "../../components/Icon";
import { BaseTooltip } from "../../components/Tooltip/BaseTooltip";
import { getShowConfidenceScore } from "../../constants/Endpoints";
import { FEATURE, VALIDATOR_MODE_FEATURE } from "../../constants/FeatureFlag";
import { TERMINATE_ON } from "../../constants/Features";
import { ResultFile, ValidatorFileContext } from "../../hooks/useFile";
import ConfidenceScoreSetting from "../../molecules/confidence-score/ConfidenceScoreSetting";
import { OacJsonResponse } from "../../types/demo";
import { ExtractorOacJsonResponse } from "../../types/extractorOacResponse";
import { loadExternalScript } from "../../utils/downloadFile";
import { endpointStore } from "../../utils/mobx/EndpointStore";
import { envVarStore } from "../../utils/mobx/EnvVarStore";
import { parsingTableStore } from "../../utils/mobx/ParsingTableStore";
import { staticLabelStore } from "../../utils/mobx/StaticLabelStore";
import { cn } from "../../utils/style";

export interface SubmitResultParams {
  files: ResultFile[];
  requestId: string | null;
  token: string | null;
  userId: string | null;
}

export interface SubmitResult {
  code: number;
  message: string;
  ocrExtractResult: OacJsonResponse | null;
}

async function submitResult({
  files,
  requestId,
  token,
  userId,
}: SubmitResultParams): Promise<void> {
  await envVarStore.fetchEnvVars();
  const { backendUrl } = envVarStore;
  const { endpointType } = endpointStore;

  let oac: OacJsonResponse[];
  if (FEATURE === VALIDATOR_MODE_FEATURE && endpointType === "extractor") {
    oac = files.map(file => makeResultToExtractorOACFormat(file, file.result));
  } else {
    oac = files.map(file => file.result as OacJsonResponse);
  }

  try {
    // REACT_APP_SERVER_URL이 없으면 window.location.origin을 사용하여 기본 URL 설정
    const baseUrl = process.env.REACT_APP_SERVER_URL
      ? process.env.REACT_APP_SERVER_URL.startsWith("http")
        ? process.env.REACT_APP_SERVER_URL
        : window.location.origin + process.env.REACT_APP_SERVER_URL
      : window.location.origin + "/custom";

    const fullUrl = `${baseUrl}/ButtonEvent.js`;

    await loadExternalScript(fullUrl)
      .then(() => {
        // 외부 모듈이 로드되면 window 객체에서 함수 접근 가능
        if (window.SubmitEvent) {
          window.SubmitEvent({
            oac,
            requestId,
            token,
            userId,
            submitUrl: backendUrl,
          });
        } else {
          console.error("SubmitEvent function not found in the loaded script");
        }
      })
      .catch(error => {
        console.error("Error submitting result:", error);
      });
  } catch (error) {
    console.error("Error submitting result:", error);
  }
}

const makeResultToExtractorOACFormat = (
  data: ResultFile,
  res: any,
): ExtractorOacJsonResponse => {
  const result = data.result as ExtractorOacJsonResponse;
  const resultOacFormat = {
    code: result.code,
    filename: result.filename,
    apiVersion: result.apiVersion,
    confidence: result.confidence,
    documentType: result.documentType,
    mimeType: result.mimeType,
    modelVersion: result.modelVersion,
    stored: result.stored,
    metadata: result.metadata,
    imageBase64: [],
    fields: res,
    ontologies: [],
  };
  return resultOacFormat;
};

export interface MenuItem {
  title: string;
  description: string;
}

interface DetailFooterSkeletonProps {
  hasConfidenceScore?: boolean;
  hasGetAPIButton?: boolean;
  hasFieldEditInfo?: boolean;
  hasSubmitButton?: boolean;
}

export const DetailFooterSkeletonWithProps = ({
  hasConfidenceScore = true,
  hasFieldEditInfo = true,
  hasSubmitButton = true,
}: DetailFooterSkeletonProps) => {
  if (!hasConfidenceScore && !hasFieldEditInfo && !hasSubmitButton) {
    return null;
  }
  return (
    <div className="flex h-footer flex-1 items-center justify-between border-t border-border px-4 mobile:h-mobile-footer">
      {/* Confidence Score Setting Skeleton */}
      <div className="flex-1">
        {hasConfidenceScore && (
          <div className="flex items-center gap-2">
            <Skeleton className="h-8 w-32" />
            <Skeleton className="h-8 w-16" />
          </div>
        )}
      </div>

      {/* Right Side Actions Skeleton */}
      <div className="flex items-center gap-4">
        {/* Field Edit Info Skeleton */}
        {hasFieldEditInfo && <Skeleton className="h-5 w-20" />}

        {/* Submit Button Skeleton */}
        {hasSubmitButton && <Skeleton className="h-9 w-[90px]" />}
      </div>
    </div>
  );
};

const DetailFooter = () => {
  const { t } = useTranslation();
  const { getChanged } = parsingTableStore;
  const { endpointType } = endpointStore;
  const { files, inferenceState } = useContext(ValidatorFileContext);
  const { staticLabels } = staticLabelStore;

  const [searchParams, _] = useSearchParams();
  const requestId = searchParams.get("requestId");
  const token = searchParams.get("token");
  const userId = searchParams.get("userId");

  const hasConfidenceScore = getShowConfidenceScore(endpointType);
  const hasSubmitButton = staticLabels.Common.Button.Submit[1];
  const hasFieldEditInfo = FEATURE === VALIDATOR_MODE_FEATURE;

  const FieldEditInfo = () => {
    return (
      <span
        className={cn(getChanged ? "text-accent-green" : "text-muted-darker")}
      >
        {getChanged > 0
          ? `${getChanged} ${t("actions.edited")}`
          : t("actions.no_edited")}
      </span>
    );
  };

  const SubmitButton = () => {
    return (
      <BaseTooltip
        tooltipContent={
          //  TODO: TERMINATE_ON 옵션 필요 유무 확인 필요
          !TERMINATE_ON && (
            <TooltipContent>
              <div>{t("messages.submit_and_quit")}</div>
            </TooltipContent>
          )
        }
      >
        <Button
          className="flex gap-1.5"
          onClick={() => {
            submitResult({
              files: files,
              requestId: requestId,
              token: token,
              userId: userId,
            });
          }}
        >
          <Icon source={Icons.CheckCircle} className="size-4" />
          <span className="whitespace-nowrap">
            {staticLabels.Common.Button.Submit[0]}
          </span>
        </Button>
      </BaseTooltip>
    );
  };

  if (inferenceState.isInfering)
    return (
      <DetailFooterSkeletonWithProps
        hasConfidenceScore={hasConfidenceScore}
        hasFieldEditInfo={hasFieldEditInfo}
        hasSubmitButton={hasSubmitButton}
      />
    );

  // 모든 컴포넌트의 표시 여부 확인
  const hasAnyContent =
    hasConfidenceScore || hasFieldEditInfo || hasSubmitButton;

  // 내부가 비어있으면 null 반환
  if (!hasAnyContent) {
    return null;
  }

  return (
    <div className="flex h-footer flex-1 items-center justify-between border-t border-border px-4 mobile:h-mobile-footer">
      {/* Confidence Score Setting */}
      <div className="flex-1">
        {hasConfidenceScore && <ConfidenceScoreSetting />}
      </div>

      <div className="flex items-center gap-4">
        {/* Field Edit Info */}
        {hasFieldEditInfo && <FieldEditInfo />}
        {/* Submit Button */}
        {hasSubmitButton && <SubmitButton />}
      </div>
    </div>
  );
};

export default observer(DetailFooter);
